/**
 * Memberships API Client
 * 
 * TypeScript client for membership enrollment and management endpoints.
 */

import { supabase } from './supabase';

// In production we want same-origin requests (nginx proxies /api -> marketing-api).
// In local dev we ALSO want same-origin requests so Vite can proxy /api -> the local API.
// If you need to bypass the proxy (rare), set VITE_API_URL.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface PublicCompanyConfig {
  id: string;
  brand_key: string | null;
  domain: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  terms_url: string | null;
  enrollment_config: Record<string, any> | null;
}

export interface PublicMembershipConfigResponse {
  company: PublicCompanyConfig;
  membership_plans: any[];
  discount_codes: any[];
}

export interface SearchCustomerRequest {
  company: string;
  phone: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface SearchCustomerResponse {
  customers: Array<{
    customerId: string;
    serviceAccountId: string;
    locationId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  }>;
}

export interface CreateCustomerRequest {
  company: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  location: {
    address1: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface CreateCustomerResponse {
  customerId: string;
  serviceAccountId: string;
  locationId: string;
}

export interface GetScheduleBlocksRequest {
  company: string;
  jobClass: string;
  jobType: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

export interface GetScheduleBlocksResponse {
  availableSlots: Array<{
    date: string;
    window: string;
    technicianId?: string;
    available: boolean;
  }>;
}

export type MaintenanceVisitType = 'pm' | 'other';

export interface ScheduleMaintenanceRequest {
  membership_id: string;
  visit_date: string;
  visit_window: string;
  visit_type: MaintenanceVisitType;
  equipment_age?: string;
  priority_level?: number;
}

export interface ScheduleMaintenanceVisit {
  id: string;
  membership_id: string;
  company_id: string;
  visit_date: string;
  visit_window: string;
  visit_type: MaintenanceVisitType;
  job_id?: string | null;
  job_number?: string | null;
  source?: string | null;
  status?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
}

export interface ScheduleMaintenanceResponse {
  success: boolean;
  company_id: string;
  membership_id: string;
  membership_number: string;
  visit: ScheduleMaintenanceVisit;
  job_number: string | null;
  visits_remaining: number;
}

export interface CreateJobRequest {
  company: string;
  serviceAccountId: string;
  locationId: string;
  jobClass: string;
  jobType: string;
  category: string;
  departmentId: string;
  leadSourceId: string;
  leadSourceTypeId: string;
  estimatedDuration?: number;
}

export interface CreateJobResponse {
  jobId: string;
  jobNumber: string;
}

export type MembershipEnrollmentSource =
  | 'membership_signup_site'
  | 'contact_center'
  | 'admin'
  | 'tech_portal'
  | 'elevenlabs_voice';

export interface EnrollmentRequest {
  company_id: string;
  plan_id: string;
  plan_code: string;
  plan_name: string;
  /** How the membership was sold (signup site vs contact center vs admin) */
  enrollment_source?: MembershipEnrollmentSource;
  /** Used by contact center + ElevenLabs voice agent to record attribution */
  sold_by_agent_login?: string;
  billing_frequency: 'monthly' | 'annual';
  tune_ups_per_year: 1 | 2;
  systems_count: number;
  base_price_cents: number;
  discount_code_id?: string;
  discount_amount_cents?: number;
  final_price_cents: number;
  customer: {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  successware: {
    customer_id: string;
    service_account_id: string;
    location_id: string;
  };
  /** @deprecated Use payment_method_id + stax_customer_id instead (new Stax.js flow) */
  payment_token?: string;
  /** New Stax.js flow: payment method ID returned by tokenize() */
  payment_method_id?: string;
  /** New Stax.js flow: Stax customer ID returned by tokenize() */
  stax_customer_id?: string;
  terms_accepted: {
    timestamp: string;
    ip_address?: string;
    terms_version?: string;
    signature?: {
      data_url: string;
      signed_name?: string;
      signed_at?: string;
    };
  };
}

export interface EnrollmentResponse {
  success: boolean;
  membership_id: string;
  stax_customer_id: string;
  stax_subscription_id: string;
  transaction_id: string;
  next_billing_date: string;
}

export type AdminMembershipStatus =
  | 'pending_payment'
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'expired'
  | 'cancelled';

export interface VoiceSaleMetadata {
  provider: 'elevenlabs';
  agent_id?: string | null;
  agent_name?: string | null;
  conversation_id?: string | null;
  call_id?: string | null;
}

export interface MembershipSaleMetadata {
  source?: MembershipEnrollmentSource;
  sold_by_agent_login?: string | null;
  sold_by_technician_id?: string | null;
  sold_by_technician_name?: string | null;
  sold_by_technician_email?: string | null;
  sold_by_employee_code?: string | null;
  voice?: VoiceSaleMetadata | null;
}

export interface MembershipMetadata {
  sale?: MembershipSaleMetadata;
  [key: string]: unknown;
}

export interface AdminMembershipSummary {
  id: string;
  membership_number: string;
  company_id: string;
  enrollment_source?: MembershipEnrollmentSource;
  metadata?: MembershipMetadata | null;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  customer_phone: string;
  plan_id: string;
  plan_code: string;
  plan_name: string;
  status: AdminMembershipStatus;
  billing_frequency: 'monthly' | 'annual';
  final_price_cents: number;
  start_date?: string;
  next_billing_date?: string;
  visits_used?: number;
  total_visits_allowed?: number;
  visits_remaining?: number;
  deferred_revenue_balance_cents?: number;
  deferred_revenue_payments_cents?: number;
  deferred_revenue_maintenance_value_cents?: number;
  deferred_revenue_maintenance_visits_completed?: number;
  deferred_revenue_maintenance_cost_cents?: number;
  created_at?: string;
  sale_source?: string | null;
  sold_by_agent_login?: string | null;
  sold_by_technician_name?: string | null;
  sold_by_technician_email?: string | null;
}

export interface AdminMembershipTransaction {
  id: string;
  membership_id: string;
  transaction_type: string;
  amount_cents: number;
  status: string;
  transaction_date?: string;
  description?: string;
  stax_transaction_id?: string;
  invoice_number?: string;
  card_last_four?: string;
  card_brand?: string;
}

export interface AdminMembershipVisit {
  id: string;
  membership_id: string;
  visit_date: string;
  visit_window?: string;
  visit_type?: string;
  status?: string;
  notes?: string;
  successware_job_number?: string;
}

export interface AvailableSlot {
  date: string;
  window: string;
  technicianId?: string;
  available?: boolean;
}

export interface FetchAdminMembershipsParams {
  search?: string;
  status?: AdminMembershipStatus | '';
  company_id?: string;
  limit?: number;
  offset?: number;
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  // Admin endpoints require a logged-in marketing user.
  if (endpoint.startsWith('/api/admin/')) {
    const {
      data: { session },
      error: sessionErr,
    } = await supabase.auth.getSession();

    if (sessionErr) {
      throw sessionErr;
    }

    const token = session?.access_token;
    if (!token) {
      throw new Error('Missing auth token. Please sign in again.');
    }

    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const rawText = await response.text().catch(() => '');
  let parsed: unknown = null;

  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // Non-JSON response (e.g. nginx 502 HTML page)
      parsed = null;
    }
  }

  const getMessage = (data: unknown) => {
    if (!data || typeof data !== 'object') return null;
    const msg = (data as { message?: unknown }).message;
    const err = (data as { error?: unknown }).error;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (typeof err === 'string' && err.trim()) return err;
    return null;
  };

  if (!response.ok) {
    const msgFromJson = getMessage(parsed);
    const msgFromText = rawText ? rawText.trim().slice(0, 200) : null;
    throw new Error(msgFromJson || msgFromText || `Request failed (${response.status})`);
  }

  if (parsed === null) {
    throw new Error(`Invalid JSON response (${response.status})`);
  }

  return parsed as T;
}

export async function searchCustomer(request: SearchCustomerRequest): Promise<SearchCustomerResponse> {
  const data = await apiRequest<unknown>('/api/memberships/search-customer', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  let customers: unknown = data;

  // Backend currently returns a raw array; normalize so callers consistently get { customers: [...] }.
  if (data && typeof data === 'object' && !Array.isArray(data) && 'customers' in data) {
    const { customers: customersFromObject } = data as { customers?: unknown };
    customers = customersFromObject;
  }

  const rawCustomers = Array.isArray(customers) ? (customers as unknown[]) : [];

  const normalized = rawCustomers
    .map((row): SearchCustomerResponse['customers'][number] | null => {
      if (!row || typeof row !== 'object') return null;
      const r = row as Record<string, unknown>;

      // The SuccessWare webhook returns a nested shape:
      // { serviceAccountId, customerId, locationId, customer: {...}, location: {...} }
      const nestedCustomer =
        r.customer && typeof r.customer === 'object' && !Array.isArray(r.customer)
          ? (r.customer as Record<string, unknown>)
          : null;
      const nestedLocation =
        r.location && typeof r.location === 'object' && !Array.isArray(r.location)
          ? (r.location as Record<string, unknown>)
          : null;

      const getStringFrom = (src: Record<string, unknown> | null, ...keys: string[]) => {
        if (!src) return '';
        for (const key of keys) {
          const v = src[key];
          if (typeof v === 'string' && v.trim()) return v;
        }
        return '';
      };

      const getString = (...keys: string[]) => {
        return (
          getStringFrom(r, ...keys) ||
          getStringFrom(nestedCustomer, ...keys) ||
          getStringFrom(nestedLocation, ...keys)
        );
      };

      // SuccessWare webhook payloads have drifted over time; support common key variants.
      const customerId = getString('customerId', 'customer_id', 'CustomerId', 'CustomerID');
      const serviceAccountId = getString(
        'serviceAccountId',
        'service_account_id',
        'serviceAccountID',
        'ServiceAccountId',
        'ServiceAccountID',
      );
      const locationId = getString('locationId', 'location_id', 'LocationId', 'LocationID');

      let firstName = getString('firstName', 'first_name', 'FirstName');
      let lastName = getString('lastName', 'last_name', 'LastName');

      const fullName = getString('fullName', 'full_name', 'name', 'customerName', 'customer_name', 'CustomerName');
      if (!firstName && !lastName && fullName) {
        const parts = fullName.trim().split(/\s+/);
        firstName = parts.shift() || '';
        lastName = parts.join(' ');
      }

      const email = getString('email', 'Email');
      const phone = getString('phone', 'phone_number', 'phoneNumber', 'PhoneNumber', 'phone_number_formatted');
      const address = getString(
        'fullAddress',
        'address',
        'address1',
        'address_1',
        'Address',
        'Address1',
        'streetAddress',
        'street_address',
        'addressLine1',
      );
      const city = getString('city', 'City');
      const state = getString('state', 'State');
      const zip = getString('zip', 'zipCode', 'zip_code', 'postalCode', 'postal_code', 'Zip');

      return {
        customerId,
        serviceAccountId,
        locationId,
        firstName,
        lastName,
        email,
        phone,
        address,
        city,
        state,
        zip,
      };
    })
    .filter((c): c is SearchCustomerResponse['customers'][number] => Boolean(c));

  return {
    customers: normalized,
  };
}

export async function createCustomer(request: CreateCustomerRequest): Promise<CreateCustomerResponse> {
  return apiRequest<CreateCustomerResponse>('/api/memberships/create-customer', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getScheduleBlocks(request: GetScheduleBlocksRequest): Promise<GetScheduleBlocksResponse> {
  return apiRequest<GetScheduleBlocksResponse>('/api/memberships/get-schedule-blocks', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function scheduleMaintenance(
  request: ScheduleMaintenanceRequest
): Promise<ScheduleMaintenanceResponse> {
  return apiRequest<ScheduleMaintenanceResponse>('/api/memberships/schedule-maintenance', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function createJob(request: CreateJobRequest): Promise<CreateJobResponse> {
  return apiRequest<CreateJobResponse>('/api/memberships/create-job', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function enrollMembership(request: EnrollmentRequest): Promise<EnrollmentResponse> {
  let headers: Record<string, string> | undefined;

  // Technician portal enrollments require a valid Supabase Auth bearer token.
  if (request.enrollment_source === 'tech_portal') {
    const {
      data: { session },
      error: sessionErr,
    } = await supabase.auth.getSession();

    if (sessionErr) {
      throw sessionErr;
    }

    const token = session?.access_token;
    if (!token) {
      throw new Error('Missing auth token. Please sign in again.');
    }

    headers = { Authorization: `Bearer ${token}` };
  }

  return apiRequest<EnrollmentResponse>('/api/memberships/enroll', {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });
}

export async function updatePaymentMethod(membershipId: string, paymentToken: string): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('/api/memberships/update-payment-method', {
    method: 'POST',
    body: JSON.stringify({
      membership_id: membershipId,
      payment_token: paymentToken,
    }),
  });
}

export async function fetchAdminMemberships(params: FetchAdminMembershipsParams = {}): Promise<AdminMembershipSummary[]> {
  const searchParams = new URLSearchParams();

  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.company_id) searchParams.set('company_id', params.company_id);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));

  const query = searchParams.toString();
  const path = query ? `/api/admin/memberships?${query}` : '/api/admin/memberships';

  const response = await apiRequest<{ memberships: AdminMembershipSummary[] }>(path, {
    method: 'GET',
  });

  return response.memberships;
}

export async function fetchAdminMembershipDetail(id: string): Promise<{
  membership: AdminMembershipSummary;
  transactions: AdminMembershipTransaction[];
  visits: AdminMembershipVisit[];
}> {
  return apiRequest(`/api/admin/memberships/${id}`, {
    method: 'GET',
  });
}

export interface CancelAdminMembershipRequest {
  reason?: string;
  payoff_amount_cents?: number;
  override_no_payment?: boolean;
}

export async function cancelMembership(id: string, payload: CancelAdminMembershipRequest = {}): Promise<{ success: boolean }> {
  return apiRequest(`/api/admin/memberships/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchAvailableSlots(membershipId: string, days = 30): Promise<{ availableSlots: AvailableSlot[] }> {
  const path = `/api/admin/memberships/${membershipId}/available-slots?days=${days}`;
  return apiRequest(path, { method: 'GET' });
}

export interface BookVisitRequest {
  visit_date: string;
  visit_window: string;
  visit_type: 'pm' | 'repair' | 'emergency';
  notes?: string;
}

export async function bookVisit(membershipId: string, payload: BookVisitRequest): Promise<{ success: boolean; visit?: any; job_number?: string }> {
  return apiRequest(`/api/admin/memberships/${membershipId}/book-visit`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function stopAutoRenew(membershipId: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/admin/memberships/${membershipId}/stop-auto-renew`, {
    method: 'POST',
  });
}

export async function payoffMembership(membershipId: string, reason?: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/admin/memberships/${membershipId}/payoff`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function updateMembershipStatus(
  membershipId: string,
  status: AdminMembershipStatus,
  reason?: string,
): Promise<{ success: boolean }> {
  return apiRequest(`/api/admin/memberships/${membershipId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, reason }),
  });
}

export async function updatePaymentMethodAdmin(membershipId: string, paymentToken: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/admin/memberships/${membershipId}/update-payment`, {
    method: 'POST',
    body: JSON.stringify({ payment_token: paymentToken }),
  });
}

export async function getPublicMembershipConfig(domain: string): Promise<PublicMembershipConfigResponse> {
  const qs = new URLSearchParams({ domain }).toString();
  return apiRequest<PublicMembershipConfigResponse>(`/api/memberships/public-config?${qs}`, {
    method: 'GET',
  });
}
